import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { doc, updateDoc, getDoc } from "firebase/firestore";

import { GradientSpan, H1, P2, P3 } from "./theme/text/Headings";
import { InsightsContainer, InsightsBody } from "./theme/QuizBox";
import { Grey, OffWhite } from "./theme/colors";

import openAIService from "../services/OpenAIService";
import { db } from "../services/firebase.config";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  animation: ${pulse} 2s infinite;
`;

const PersonalityInsights = ({
  quizResults,
  quizType,
  colors,
  onInsightsGenerated,
  quizId,
  forceRegenerate,
}) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastRegenerateTimestamp = useRef(null);
  const isRegenerating = useRef(false);

  // Check for existing insights first
  const loadExistingInsights = async () => {
    if (!quizId) return null;

    try {
      const quizDocRef = doc(db, "quiz-answers", quizId);
      const quizDoc = await getDoc(quizDocRef);

      if (quizDoc.exists() && quizDoc.data().insights) {
        return quizDoc.data().insights.content;
      }
    } catch (err) {
      console.warn("Could not load existing insights:", err);
    }
    return null;
  };

  const generateInsights = useCallback(async () => {
    if (!quizResults || quizResults.length === 0) {
      setError("No quiz results available to analyze");
      return Promise.resolve();
    }
    if (!quizId) {
      setError("Quiz ID required to save insights");
      return Promise.resolve();
    }

    setLoading(true);
    setError(null);
    try {
      const result = await openAIService.generatePersonalityInsights(
        quizResults,
        quizType
      );

      // Save insights to the quiz document root
      const quizDocRef = doc(db, "quiz-answers", quizId);
      await updateDoc(quizDocRef, {
        insights: {
          content: result.insights,
          generated: new Date().toISOString(),
          quizType: quizType,
        },
      });

      setInsights(result.insights);
      if (onInsightsGenerated) {
        onInsightsGenerated(result.insights);
      }
    } catch (err) {
      console.error("Error generating insights:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [quizResults, quizType, quizId, onInsightsGenerated]);

  // Auto-generate insights when component mounts with valid data
  useEffect(() => {
    const initializeInsights = async () => {
      if (
        quizResults &&
        quizResults.length > 0 &&
        quizId &&
        !insights &&
        !loading
      ) {
        // Check for existing insights first
        const existingInsights = await loadExistingInsights();
        if (existingInsights) {
          setInsights(existingInsights);
          if (onInsightsGenerated) {
            onInsightsGenerated(existingInsights);
          }
        } else {
          // Generate new insights if none exist
          generateInsights();
        }
      }
    };

    initializeInsights();
    // eslint-disable-next-line
  }, [
    quizResults,
    quizId,
    insights,
    loading,
    generateInsights,
    onInsightsGenerated,
  ]);

  // Handle forced regeneration from parent
  useEffect(() => {
    if (
      forceRegenerate &&
      forceRegenerate !== lastRegenerateTimestamp.current &&
      quizResults &&
      quizResults.length > 0 &&
      quizId &&
      !isRegenerating.current
    ) {
      lastRegenerateTimestamp.current = forceRegenerate;
      isRegenerating.current = true;

      // Clear existing insights to force regeneration
      setInsights(null);

      // Generate new insights
      generateInsights().finally(() => {
        isRegenerating.current = false;
      });
    }
  }, [forceRegenerate, quizResults, quizId, generateInsights]);

  if (loading) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <LoadingContainer>
            <H1 color={colors?.background || Grey}>
              🤖 AI is analyzing your personality...
            </H1>
            <P3 color={Grey} style={{ marginTop: "10px" }}>
              This usually takes 3-5 seconds
            </P3>
          </LoadingContainer>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  if (error) {
    return (
      <InsightsContainer>
        <InsightsBody>
          <H1 color={Grey}>Unable to generate insights at this time</H1>
          <P3 color={Grey} style={{ marginTop: "10px" }}>
            {error}
          </P3>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  if (insights) {
    return (
      <InsightsContainer className="text-align-left">
        <InsightsBody>
          <P2 style={{ marginBottom: "10px", fontWeight: "900" }}>
            <GradientSpan
              $background={colors?.background}
              $transitionBackground={colors?.transitionBackground}
            >
              Your Insights
            </GradientSpan>
          </P2>
          <P2 color={OffWhite}>{insights}</P2>
        </InsightsBody>
      </InsightsContainer>
    );
  }

  // Loading state while waiting for data
  return (
    <InsightsContainer>
      <InsightsBody>
        <LoadingContainer>
          <H1 color={colors?.background || Grey}>Preparing your insights...</H1>
        </LoadingContainer>
      </InsightsBody>
    </InsightsContainer>
  );
};

export default PersonalityInsights;
